import { MapPin, Video } from 'lucide-react';
import type { CalendarEvent } from '@/types';
import { Button } from '@/components/ui';
import { hapticTap } from '@/features/lifecycle/haptics';
import { meetingUrlFor, openMeetingUrl } from '@/lib/meetingLink';
import { formatTimeRange } from '@/lib/time';
import { NowProgressBar, NowStatusPill, NowTitle } from './NowShell';
import { progressPct } from './todaySelectors';

export interface NowMeetingCardProps {
  event: CalendarEvent;
  now: Date;
}

/**
 * Meeting equivalent of NowCard. Shown when a calendar event's window contains
 * `now` and no task or habit claims the hero slot — previously an ongoing
 * meeting never surfaced here and the Today view read "Nothing happening right
 * now" mid-meeting. Calendar events are read-only, so the card is informational.
 */
export function NowMeetingCard({ event, now }: NowMeetingCardProps) {
  const pct = progressPct(event.start, event.end, now);
  const remainingMin = Math.max(0, Math.ceil((event.end.getTime() - now.getTime()) / 60_000));
  const joinUrl = meetingUrlFor(event);

  return (
    <article className="rounded-2xl border border-line-2 bg-card p-4">
      <NowStatusPill hue="info" className="mb-2">
        In a meeting
      </NowStatusPill>

      <NowTitle className="line-clamp-2 break-words">{event.title}</NowTitle>

      <div className="mt-2 font-mono tabular-nums text-body-lg font-medium leading-tight text-t1">
        {formatTimeRange(event.start, event.end)}
      </div>
      <div className="mt-0.5 font-mono text-2xs tabular-nums text-t3">
        {remainingMin > 0 ? `${remainingMin}m left` : 'Wrapping up'}
      </div>

      {event.location && (
        <p className="mt-2 flex items-center gap-1.5 text-caption text-t2">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-t3" aria-hidden />
          <span className="truncate">{event.location}</span>
        </p>
      )}

      <NowProgressBar pct={pct} className="mt-3" fillClassName="bg-info" />

      {joinUrl && (
        <Button
          size="md"
          variant="primary"
          className="mt-3 w-full"
          leadingIcon={<Video className="h-4 w-4" aria-hidden />}
          onClick={() => {
            void hapticTap();
            void openMeetingUrl(joinUrl);
          }}
        >
          Join meeting
        </Button>
      )}
    </article>
  );
}
